/*
 * Pure helpers (mirrored from scripts/ring-sizer/src/size-mapping.js).
 * If you change these, update scripts/ring-sizer/src/size-mapping.js AND its tests.
 */
const RING_US_TABLE = [
  { mm: 14.07, label: '3' },   { mm: 14.47, label: '3½' },  { mm: 14.88, label: '4' },
  { mm: 15.28, label: '4½' },  { mm: 15.69, label: '5' },   { mm: 16.10, label: '5½' },
  { mm: 16.50, label: '6' },   { mm: 16.91, label: '6½' },  { mm: 17.31, label: '7' },
  { mm: 17.72, label: '7½' },  { mm: 18.12, label: '8' },   { mm: 18.53, label: '8½' },
  { mm: 18.94, label: '9' },   { mm: 19.34, label: '9½' },  { mm: 19.75, label: '10' },
  { mm: 20.15, label: '10½' }, { mm: 20.56, label: '11' },  { mm: 20.97, label: '11½' },
  { mm: 21.37, label: '12' },  { mm: 21.78, label: '12½' }, { mm: 22.18, label: '13' },
];

function mmToSize(diameterMm) {
  if (typeof diameterMm !== 'number' || isNaN(diameterMm) || diameterMm <= 0) {
    return { eu: null, it: null, us: null, circumferenceMm: null };
  }
  const circ = diameterMm * Math.PI;
  let bestUs = null, bestDiff = Infinity;
  for (const row of RING_US_TABLE) {
    const d = Math.abs(row.mm - diameterMm);
    if (d < bestDiff) { bestDiff = d; bestUs = row.label; }
  }
  if (bestDiff > 0.45) bestUs = null;
  return { eu: Math.round(circ), it: Math.round(circ - 40), us: bestUs, circumferenceMm: circ };
}

function parseSizeToMm(sizeStr, format) {
  if (typeof sizeStr !== 'string') return NaN;
  if (format === 'dual') {
    const matches = sizeStr.match(/\d+(?:[.,]\d+)?/g);
    if (!matches) return NaN;
    return Math.max(...matches.map((m) => parseFloat(m.replace(',', '.'))));
  }
  const num = parseFloat(sizeStr.replace(',', '.'));
  if (isNaN(num)) return NaN;
  if (format === 'eu') return num;
  if (format === 'it') return num + 40;
  return NaN;
}

// Gemella di scripts/ring-sizer/src/size-mapping.js, dove e' testata.
// La tabella mostra la misura VERA del dito; il consiglio mostra una taglia che
// ESISTE per quell'anello. Divergono quando l'anello e' prodotto solo in certe
// taglie — Pandora fa solo le pari — e allora sullo schermo compaiono due numeri
// diversi a due righe di distanza. Il calcolo e' giusto, ma va detto a parole.
function divergeDallaMisura(diameterMm, closestValue, format) {
  const misura = mmToSize(diameterMm).eu;
  const consigliata = parseSizeToMm(closestValue, format);
  if (misura === null || !Number.isFinite(consigliata)) return false;
  return Math.round(consigliata) !== misura;
}

function findClosest(diameterMm, sizes, format) {
  if (!sizes || sizes.length === 0) {
    return { value: null, available: false, diffMm: Infinity, outOfRange: false };
  }
  const targetCirc = diameterMm * Math.PI;
  let best = null, bestDiff = Infinity;
  for (const sz of sizes) {
    const c = parseSizeToMm(sz.value, format);
    if (isNaN(c)) continue;
    const d = Math.abs(c - targetCirc);
    if (d < bestDiff) { bestDiff = d; best = sz; }
  }
  if (!best) {
    return { value: null, available: false, diffMm: Infinity, outOfRange: false };
  }
  return {
    value: best.value,
    available: best.available,
    diffMm: bestDiff,
    outOfRange: bestDiff > 1.5,
  };
}

/*
 * Ring Sizer Drawer — custom element <cz-ring-sizer>.
 * Wizard guida taglie (method picker, calibrazione carta, sizer/spago, risultato).
 *
 * API:
 *   document.querySelector('cz-ring-sizer').open({ availableSizes, format, onSelect })
 *
 * State machine:
 *   state.step ∈ { 'method', 'calibrate', 'measure', 'result' }
 */
(function () {
  if (customElements.get('cz-ring-sizer')) return;

  class CzRingSizer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-ring-sizer-dialog]');
      this.body = this.querySelector('[data-ring-sizer-body]');
      this.footer = this.querySelector('[data-ring-sizer-footer]');
      this.dots = this.querySelector('[data-ring-sizer-dots]');
      this.state = null;
      this.bindGlobalListeners();
    }

    bindGlobalListeners() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-ring-sizer-close]')) this.close();
      });
      this.dialog.addEventListener('close', () => {
        this.state = null;
      });
      // Click sul backdrop chiude (il backdrop è "fuori" dal panel interno)
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });
    }

    open({ availableSizes, format, onSelect }) {
      this.state = {
        step: 'method',
        method: null,
        pxPerMm: this.loadPxPerMm(),
        diameterMm: null,
        availableSizes: availableSizes || [],
        format: format || 'eu',
        onSelect: onSelect || (() => {}),
      };
      // Default suggestion based on device: pointer:fine = desktop → spago, altrimenti screen
      this.state.method = window.matchMedia('(pointer: fine)').matches ? 'string' : 'screen';
      this.render();
      this.dialog.showModal();
    }

    close() {
      this.dialog.close();
    }

    goTo(step) {
      this.state.step = step;
      this.render();
    }

    loadPxPerMm() {
      try {
        const v = parseFloat(localStorage.getItem('cz.ringSizer.pxPerMm'));
        return v >= 0.5 && v <= 20 ? v : null;
      } catch { return null; }
    }

    savePxPerMm(v) {
      try { localStorage.setItem('cz.ringSizer.pxPerMm', String(v)); } catch {}
    }

    // Barra di regolazione guidata a mano: su iOS l'<input type=range> nativo si
    // muove solo prendendo esattamente il pallino, e dentro il body che scrolla il
    // gesto viene spesso preso dallo scroll. Qui ogni tocco sulla barra (anche sulla
    // traccia) sposta il valore e il trascinamento lo segue; la tastiera resta nativa.
    bindSlider(slider, onValue, { onStart, onEnd } = {}) {
      const min = parseFloat(slider.min);
      const max = parseFloat(slider.max);
      const step = parseFloat(slider.step) || 1;
      const decimals = (String(slider.step).split('.')[1] || '').length;
      const valueAt = (clientX) => {
        const rect = slider.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const raw = min + ratio * (max - min);
        return parseFloat((Math.round(raw / step) * step).toFixed(decimals));
      };
      const onMove = (e) => onValue(valueAt(e.clientX));
      const onUp = (e) => {
        slider.removeEventListener('pointermove', onMove);
        slider.removeEventListener('pointerup', onUp);
        slider.removeEventListener('pointercancel', onUp);
        if (slider.hasPointerCapture?.(e.pointerId)) slider.releasePointerCapture(e.pointerId);
        onEnd?.();
      };
      slider.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        slider.focus({ preventScroll: true });
        try { slider.setPointerCapture?.(e.pointerId); } catch {}
        slider.addEventListener('pointermove', onMove);
        slider.addEventListener('pointerup', onUp);
        slider.addEventListener('pointercancel', onUp);
        onStart?.();
        onValue(valueAt(e.clientX));
      });
      slider.addEventListener('input', () => onValue(parseFloat(slider.value)));
      slider.addEventListener('blur', () => onEnd?.());
    }

    render() {
      switch (this.state.step) {
        case 'method':    return this.renderMethod();
        case 'calibrate': return this.renderCalibrate();
        case 'measure':   return this.renderMeasure();
        case 'result':    return this.renderResult();
        default:
          this.body.innerHTML = `
            <div class="ring-sizer__dots" data-ring-sizer-dots></div>
            <p>Step: ${this.state.step}</p>
          `;
          this.dots = this.querySelector('[data-ring-sizer-dots]');
          this.renderDots();
          this.footer.innerHTML = '';
      }
    }

    renderDots() {
      const order = ['method', 'measure', 'result'];
      const current = this.state.step === 'calibrate' ? 'measure' : this.state.step;
      const currentIdx = order.indexOf(current);
      this.dots.innerHTML = order
        .map((_, i) => {
          const st = i < currentIdx ? 'done' : i === currentIdx ? 'on' : 'off';
          return `<div class="ring-sizer__dot" data-state="${st}"></div>`;
        })
        .join('');
    }

    renderMethod() {
      const m = this.state.method;
      this.body.innerHTML = `
        <div class="ring-sizer__dots" data-ring-sizer-dots></div>
        <p class="ring-sizer__eyebrow">Guida alle misure</p>
        <h3 class="ring-sizer__step-h"><em>Scegli</em> come misurare</h3>
        <p class="ring-sizer__step-p">Due metodi semplici per trovare la tua taglia, su qualsiasi anello.</p>
        <div class="ring-sizer__method-list">
          <button type="button" class="ring-sizer__method"
                  data-method="screen" aria-pressed="${m === 'screen'}">
            <span class="ring-sizer__method-radio" aria-hidden="true"></span>
            <span>
              <span class="ring-sizer__method-name">Appoggia un anello sullo schermo</span>
              <span class="ring-sizer__method-desc">Hai già un anello che ti sta bene? Lo metti sopra lo schermo e calibri un cerchio.</span>
            </span>
          </button>
          <button type="button" class="ring-sizer__method"
                  data-method="string" aria-pressed="${m === 'string'}">
            <span class="ring-sizer__method-radio" aria-hidden="true"></span>
            <span>
              <span class="ring-sizer__method-name">Misura il dito con uno spago</span>
              <span class="ring-sizer__method-desc">Avvolgi uno spago, segna il punto e misuri col righello.</span>
            </span>
          </button>
        </div>
      `;
      this.dots = this.querySelector('[data-ring-sizer-dots]');
      this.renderDots();

      this.footer.innerHTML = `
        <button type="button" class="ring-sizer__cta" data-action="continue" ${m ? '' : 'disabled'}>Continua</button>
      `;

      this.body.querySelectorAll('[data-method]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.state.method = btn.dataset.method;
          this.renderMethod();
        });
      });

      this.footer.querySelector('[data-action="continue"]').addEventListener('click', () => {
        if (!this.state.method) return;
        if (this.state.method === 'string') {
          this.goTo('measure');
        } else if (this.state.pxPerMm) {
          this.goTo('measure');
        } else {
          this.goTo('calibrate');
        }
      });
    }

    renderCalibrate() {
      // ISO/IEC 7810 ID-1: 85.60 × 53.98 mm.
      // Carta sempre orientata in verticale (lato lungo 85.60 sull'asse Y) —
      // coerente col drawer che è anch'esso verticale (max-width 480px).
      const widthMm = 53.98;
      const heightMm = 85.60;
      const ASPECT = widthMm / heightMm;
      // Cap statico a 400px per coprire iPhone SE/Pro/ProMax retina ad alta densità
      // (su iPhone 15 Pro 54mm reali ≈ 297 CSS px; iPhone SE ≈ 349 CSS px).
      // Se la carta supera l'altezza body, il body scrolla; lo slider resta nel footer.
      const minWidthPx = 120;
      const maxWidthPx = 400;
      const initialWidthPx = Math.max(
        minWidthPx,
        Math.min(this.body.clientWidth * 0.55, 220)
      );
      const initialHeightPx = initialWidthPx / ASPECT;

      this.body.innerHTML = `
        <div class="ring-sizer__dots" data-ring-sizer-dots></div>
        <p class="ring-sizer__eyebrow">Passo 1 di 2 · Calibrazione</p>
        <h3 class="ring-sizer__step-h">Appoggia una <em>carta</em> di credito</h3>
        <p class="ring-sizer__step-p">Tieni la carta sullo schermo in verticale e muovi la barra qui sotto (o trascina l'angolo) finché il rettangolo combacia con i bordi. Funziona anche con bancomat o tessera sanitaria.</p>

        <div class="ring-sizer__card-stage">
          <div class="ring-sizer__card" data-card
               style="width:${initialWidthPx}px; height:${initialHeightPx}px;">
            <div class="ring-sizer__card-handle" data-card-handle aria-label="Trascina per ridimensionare"></div>
          </div>
        </div>
        <p class="ring-sizer__card-meta">Carta standard ISO · 85,60 × 53,98 mm</p>
      `;
      this.dots = this.querySelector('[data-ring-sizer-dots]');
      this.renderDots();

      // Slider + hint vivono nel footer (sempre visibili, fuori dal body scroll).
      this.footer.innerHTML = `
        <div class="ring-sizer__calibrate-controls">
          <input type="range" class="ring-sizer__slider" data-card-slider
                 min="${minWidthPx}" max="${maxWidthPx}" step="1" value="${Math.round(initialWidthPx)}">
          <p class="ring-sizer__hint">Tieni la carta in verticale e fai combaciare i bordi</p>
          <div class="ring-sizer__calibrate-actions">
            <button type="button" class="ring-sizer__cta ring-sizer__cta--ghost" data-action="back">Indietro</button>
            <button type="button" class="ring-sizer__cta" data-action="confirm">Conferma</button>
          </div>
        </div>
      `;

      const card = this.body.querySelector('[data-card]');
      const handle = this.body.querySelector('[data-card-handle]');
      const slider = this.footer.querySelector('[data-card-slider]');

      const setWidth = (w) => {
        const clamped = Math.max(minWidthPx, Math.min(maxWidthPx, w));
        card.style.width = clamped + 'px';
        card.style.height = (clamped / ASPECT) + 'px';
        slider.value = String(Math.round(clamped));
      };

      // Focus mode: durante l'interazione (handle drag o slider touch) nascondiamo
      // il chrome (testi, dots, bottoni, hint) per dare massimo spazio alla card.
      const setAdjusting = (on) => this.classList.toggle('is-adjusting', on);

      // Handle drag — pointer events with setPointerCapture so up/cancel fire reliably.
      // Carta in portrait: drag verticale comanda il lato lungo, ricava il width via ASPECT.
      const bodyEl = this.body;
      let startY = 0, startW = 0;
      const onMove = (e) => {
        const deltaY = e.clientY - startY;
        setWidth(startW + deltaY * ASPECT);
        // Auto-scroll del body per tenere l'handle visibile sopra il footer fisso
        const handleRect = handle.getBoundingClientRect();
        const bodyRect = bodyEl.getBoundingClientRect();
        const margin = 24;
        if (handleRect.bottom > bodyRect.bottom - margin) {
          bodyEl.scrollTop += handleRect.bottom - (bodyRect.bottom - margin);
        }
      };
      const onUp = (e) => {
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        handle.removeEventListener('pointercancel', onUp);
        if (handle.hasPointerCapture?.(e.pointerId)) handle.releasePointerCapture(e.pointerId);
        setAdjusting(false);
      };
      handle.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        startY = e.clientY;
        startW = card.offsetWidth;
        handle.setPointerCapture?.(e.pointerId);
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
        setAdjusting(true);
      });

      this.bindSlider(slider, setWidth, {
        onStart: () => setAdjusting(true),
        onEnd: () => setAdjusting(false),
      });

      const returnTo = this.state.calibrateReturnTo || 'method';
      this.footer.querySelector('[data-action="back"]').addEventListener('click', () => {
        this.state.calibrateReturnTo = null;
        this.goTo(returnTo);
      });
      this.footer.querySelector('[data-action="confirm"]').addEventListener('click', () => {
        const widthPx = card.offsetWidth;
        const pxPerMm = widthPx / widthMm;
        this.savePxPerMm(pxPerMm);
        this.state.pxPerMm = pxPerMm;
        this.state.calibrateReturnTo = null;
        this.goTo('measure');
      });
    }

    renderMeasure() {
      if (this.state.method === 'string') return this.renderStringMeasure();

      const pxPerMm = this.state.pxPerMm;
      const initialMm = this.state.diameterMm || 17.0;

      this.body.innerHTML = `
        <div class="ring-sizer__dots" data-ring-sizer-dots></div>
        <p class="ring-sizer__eyebrow">Passo 2 di 2 · Misura</p>
        <h3 class="ring-sizer__step-h">Appoggia l'<em>anello</em> sul cerchio</h3>
        <p class="ring-sizer__step-p">Centra l'anello sul cerchio e muovi la barra (o usa − e +) finché la linea nera combacia con il bordo interno dell'anello.</p>

        <div class="ring-sizer__circle-stage">
          <div class="ring-sizer__circle" data-circle
               style="width:${initialMm * pxPerMm}px; height:${initialMm * pxPerMm}px;">
            <div class="ring-sizer__circle-handle" data-circle-handle aria-label="Trascina per ridimensionare"></div>
          </div>
        </div>

        <div class="ring-sizer__measurement">
          <div class="ring-sizer__measurement-label">Diametro interno</div>
          <div class="ring-sizer__measurement-value" data-measure-display aria-live="polite">${initialMm.toFixed(1).replace('.', ',')} mm</div>
        </div>

        <input type="range" class="ring-sizer__slider" data-circle-slider
               min="13.5" max="22.5" step="0.1" value="${initialMm}">

        <div class="ring-sizer__fine">
          <button type="button" class="ring-sizer__fine-btn" data-fine="-1" aria-label="Diminuisci 0,1 mm">−</button>
          <button type="button" class="ring-sizer__fine-btn" data-fine="1" aria-label="Aumenta 0,1 mm">+</button>
        </div>

        <button type="button" class="ring-sizer__recalibrate" data-action="recalibrate">Ricalibra lo schermo</button>
      `;
      this.dots = this.querySelector('[data-ring-sizer-dots]');
      this.renderDots();

      this.footer.innerHTML = `
        <button type="button" class="ring-sizer__cta ring-sizer__cta--ghost" data-action="back">Indietro</button>
        <button type="button" class="ring-sizer__cta" data-action="confirm">Conferma misura</button>
      `;

      const circle = this.body.querySelector('[data-circle]');
      const handle = this.body.querySelector('[data-circle-handle]');
      const slider = this.body.querySelector('[data-circle-slider]');
      const display = this.body.querySelector('[data-measure-display]');

      let currentMm = initialMm;

      const setDiameter = (mm) => {
        const clamped = Math.max(13.5, Math.min(22.5, mm));
        currentMm = clamped;
        const px = clamped * pxPerMm;
        circle.style.width = px + 'px';
        circle.style.height = px + 'px';
        slider.value = String(clamped);
        display.textContent = clamped.toFixed(1).replace('.', ',') + ' mm';
      };

      // Handle drag — pointer capture so up/cancel fire reliably even if dialog closes mid-drag.
      let startX = 0, startMm = 0;
      const onMove = (e) => {
        // Both sides of the circle grow → multiply by 2.
        const deltaPx = (e.clientX - startX) * 2;
        const deltaMm = deltaPx / pxPerMm;
        setDiameter(startMm + deltaMm);
      };
      const onUp = (e) => {
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        handle.removeEventListener('pointercancel', onUp);
        if (handle.hasPointerCapture?.(e.pointerId)) handle.releasePointerCapture(e.pointerId);
      };
      handle.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        startX = e.clientX;
        startMm = currentMm;
        handle.setPointerCapture?.(e.pointerId);
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
      });

      this.bindSlider(slider, setDiameter);

      this.body.querySelectorAll('[data-fine]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = parseInt(btn.dataset.fine, 10) * 0.1;
          setDiameter(currentMm + delta);
        });
      });

      this.body.querySelector('[data-action="recalibrate"]').addEventListener('click', () => {
        // Persist current measurement so it's restored after recalibrate round-trip
        this.state.diameterMm = currentMm;
        this.state.calibrateReturnTo = 'measure';
        this.goTo('calibrate');
      });

      this.footer.querySelector('[data-action="back"]').addEventListener('click', () => {
        this.goTo('method');
      });
      this.footer.querySelector('[data-action="confirm"]').addEventListener('click', () => {
        this.state.diameterMm = currentMm;
        this.goTo('result');
      });
    }

    renderStringMeasure() {
      const illustrationUrl = window.czRingSizerAssets?.stringIllustration || '';
      this.body.innerHTML = `
        <div class="ring-sizer__dots" data-ring-sizer-dots></div>
        <p class="ring-sizer__eyebrow">Misura con uno spago</p>
        <h3 class="ring-sizer__step-h">Avvolgi uno <em>spago</em> intorno al dito</h3>
        ${illustrationUrl ? `
          <figure class="ring-sizer__illustration">
            <img src="${illustrationUrl}" alt="Spago avvolto intorno al dito con matita che segna il punto di sovrapposizione" loading="lazy">
          </figure>
        ` : ''}
        <ol class="ring-sizer__instructions">
          <li>Avvolgi uno spago sottile intorno alla base del dito.</li>
          <li>Segna con una penna il punto di sovrapposizione.</li>
          <li>Apri lo spago e misura la lunghezza col righello.</li>
        </ol>

        <div class="ring-sizer__number-input">
          <input type="number" inputmode="numeric" min="30" max="80" step="0.5"
                 data-string-input placeholder="es. 54" aria-label="Lunghezza spago in millimetri">
          <span class="ring-sizer__number-input-unit">mm</span>
        </div>
        <p class="ring-sizer__error" data-string-error aria-live="polite"></p>
      `;
      this.dots = this.querySelector('[data-ring-sizer-dots]');
      this.renderDots();

      this.footer.innerHTML = `
        <button type="button" class="ring-sizer__cta ring-sizer__cta--ghost" data-action="back">Indietro</button>
        <button type="button" class="ring-sizer__cta" data-action="confirm">Calcola taglia</button>
      `;

      const input = this.body.querySelector('[data-string-input]');
      const errorEl = this.body.querySelector('[data-string-error]');

      this.footer.querySelector('[data-action="back"]').addEventListener('click', () => this.goTo('method'));
      this.footer.querySelector('[data-action="confirm"]').addEventListener('click', () => {
        const v = parseFloat(input.value.replace(',', '.'));
        if (isNaN(v)) {
          input.setAttribute('aria-invalid', 'true');
          errorEl.textContent = 'Inserisci la misura in millimetri.';
          return;
        }
        if (v < 30 || v > 80) {
          input.setAttribute('aria-invalid', 'true');
          errorEl.textContent = 'Controlla la misura — il dito di un adulto è tra 30 e 80 mm.';
          return;
        }
        input.removeAttribute('aria-invalid');
        errorEl.textContent = '';
        this.state.diameterMm = v / Math.PI;
        this.goTo('result');
      });
    }

    renderResult() {
      const mm = this.state.diameterMm;
      const sizes = mmToSize(mm);
      const closest = findClosest(mm, this.state.availableSizes, this.state.format);

      const mmFmt = mm.toFixed(1).replace('.', ',');
      const comparable = closest.value !== null;
      const fits = comparable && !closest.outOfRange;

      const diverge = fits && divergeDallaMisura(mm, closest.value, this.state.format);
      // Quando divergono, la frase parte dalla misura vera: dire "la tua taglia
      // e' la 54" mentre la riga sopra dice 53 attribuisce al cliente una taglia
      // che non e' la sua — e' quella dell'anello.
      const premessa = diverge
        ? `La tua misura è la ${sizes.eu}, che per questo anello non esiste. `
        : '';

      let banner = '';
      let warning = '';
      if (fits && closest.available) {
        banner = diverge
          ? `${premessa}Ti consigliamo la<b>taglia ${closest.value}</b>`
          : `Per questo anello ti consigliamo<b>la taglia ${closest.value}</b>`;
      } else if (fits) {
        banner = diverge
          ? `${premessa}La più vicina è la<b>${closest.value} — ora esaurita</b>`
          : `La tua taglia è la<b>${closest.value} — ora esaurita</b>`;
        warning = `<div class="ring-sizer__warning">Contattaci e la ordiniamo per te: <a href="#" data-action="contact">parla con la boutique</a>.</div>`;
      } else if (comparable) {
        banner = `<b>Nessuna taglia corrispondente</b>`;
        warning = `<div class="ring-sizer__warning">Questa misura non corrisponde alle taglie disponibili per questo anello. <a href="#" data-action="contact">Contattaci</a> e ti aiutiamo a trovare la taglia giusta.</div>`;
      }
      // !comparable (guardia residua): solo le misure, nessun confronto.

      this.body.innerHTML = `
        <div class="ring-sizer__dots" data-ring-sizer-dots></div>
        <div class="ring-sizer__result-mm">${mmFmt} mm</div>
        <div class="ring-sizer__result-label">Diametro interno</div>
        <div class="ring-sizer__result-table">
          <div class="ring-sizer__result-row"><span>Europea (EU)</span><b>${sizes.eu ?? '—'}</b></div>
          <div class="ring-sizer__result-row"><span>Italiana</span><b>${sizes.it ?? '—'}</b></div>
          <div class="ring-sizer__result-row"><span>USA</span><b>${sizes.us ?? '—'}</b></div>
        </div>
        ${banner ? `<div class="ring-sizer__result-banner">${banner}</div>` : ''}
        ${warning}
      `;
      this.dots = this.querySelector('[data-ring-sizer-dots]');
      this.renderDots();

      let mainCta = '';
      if (fits && closest.available) {
        mainCta = `<button type="button" class="ring-sizer__cta" data-action="use">Usa la taglia ${closest.value}</button>`;
      } else if (fits) {
        mainCta = `<button type="button" class="ring-sizer__cta" data-action="contact">Contatta la boutique</button>`;
      } else if (comparable) {
        mainCta = `<button type="button" class="ring-sizer__cta" data-action="contact">Contatta la boutique</button>`;
      }
      this.footer.innerHTML = `
        <button type="button" class="ring-sizer__cta ring-sizer__cta--ghost" data-action="restart">Ricomincia</button>
        ${mainCta}
      `;

      this.footer.querySelector('[data-action="restart"]').addEventListener('click', () => {
        this.state.diameterMm = null;
        this.goTo('method');
      });

      const useBtn = this.footer.querySelector('[data-action="use"]');
      if (useBtn) {
        useBtn.addEventListener('click', () => {
          this.state.onSelect(closest.value);
          this.close();
        });
      }

      // "Contattaci" (link nel warning e/o CTA nel footer) → pannello boutique
      this.querySelectorAll('[data-action="contact"]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
          // Attende la transition di chiusura standard Cozzari (1300ms)
          setTimeout(() => {
            const locator = document.querySelector('cz-store-locator');
            if (locator && typeof locator.open === 'function') locator.open();
          }, 1300);
        });
      });
    }
  }

  customElements.define('cz-ring-sizer', CzRingSizer);

  // Sincronizza il label visibile dei trigger SizeDrawer dopo che il <select>
  // è stato cambiato esternamente (es. dal ring sizer). Riproduce la logica
  // di _syncAllTriggerLabels in size-drawer.js per non dipendere da esso.
  function syncSizeDrawerTriggers() {
    document.querySelectorAll('[data-size-drawer-trigger]').forEach((trigger) => {
      const sourceId = trigger.id;
      const src = document.querySelector('[data-size-trigger-source="' + sourceId + '"]');
      if (!src) return;
      const selectEl = trigger.querySelector('.select');
      if (!selectEl) return;
      const selectedOpt = src.options[src.selectedIndex];
      const label = selectedOpt ? (selectedOpt.textContent || selectedOpt.value).trim() : '';
      selectEl.setAttribute('data-selected-label', label);
    });
  }

  function applySelectedSize(select, value) {
    if (!select) return;
    // Re-query by id se il select è stato rimosso dal DOM da un viewTransition
    // del product-info nel frattempo (raro ma possibile).
    if (!document.body.contains(select) && select.id) {
      const fresh = document.getElementById(select.id);
      if (fresh) select = fresh;
      else return;
    }
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    // Sync immediato + dopo il re-render async di product-info (double rAF
    // è il pattern già usato da size-drawer.js per gestire variant swap)
    syncSizeDrawerTriggers();
    requestAnimationFrame(() => {
      requestAnimationFrame(syncSizeDrawerTriggers);
    });
  }

  // Global delegated listener: any [data-ring-sizer-trigger] opens the drawer
  // reading availableSizes from the <select> referenced via data-source-select.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ring-sizer-trigger]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();

    const selectId = trigger.dataset.sourceSelect;
    const select = selectId
      ? document.getElementById(selectId)
      : trigger.closest('form, .product-form')?.querySelector('select[name^="options"]');
    if (!select) {
      console.warn('ring-sizer trigger: <select> sorgente non trovato');
      return;
    }

    const sizes = Array.from(select.querySelectorAll('option'))
      .filter((o) => o.value && o.value.trim())
      .map((o) => ({
        value: o.value.trim(),
        available: o.dataset.optionUnavailable !== 'true',
      }));
    const values = sizes.map((s) => s.value);

    const detectFormat = (s) => {
      if (!s.length) return 'eu';
      if (s.some((x) => /\(\d+\)/.test(x))) return 'dual';
      // dual col trattino: "14 - 54", "56 - 16" (due numeri separati da -)
      if (s.some((x) => /\d+(?:[.,]\d+)?\s*-\s*\d+(?:[.,]\d+)?/.test(x))) return 'dual';
      const nums = s.map((x) => parseFloat(x)).filter((n) => !isNaN(n));
      if (nums.every((n) => n >= 40 && n <= 70)) return 'eu';
      if (nums.every((n) => n >= 1 && n <= 30)) return 'it';
      return 'eu';
    };

    const ringSizer = document.querySelector('cz-ring-sizer');
    if (!ringSizer) return;

    ringSizer.open({
      availableSizes: sizes,
      format: detectFormat(values),
      onSelect: (value) => applySelectedSize(select, value),
    });
  });
})();
